import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy: Ieshia Danielle"
        description="Privacy policy and data protection terms for using iamieshiadanielle.com."
        canonical="https://iamieshiadanielle.com/privacy"
      />
      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />
        <main className="flex-1 py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto bg-white/70 backdrop-blur-md border border-[#E8DEC8] p-8 md:p-12 rounded-3xl shadow-sm">
            <p className="text-xs uppercase font-semibold tracking-widest text-[#9E1B50] mb-2">
              IESHIA DANIELLE
            </p>
            <h1 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E] mb-4">
              Privacy Policy
            </h1>
            <p className="text-xs text-[#380C1E]/70 mb-8 font-medium">
              Effective date: July 25, 2026 | Last updated: August 8, 2026
            </p>

            <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
              <p>
                This website and its related pages, including
                iamieshiadanielle.com, the shop, the quiz, the From Burnout to
                Becoming workbook and Becoming Intensive order pages, the
                speaking and booking pages, and any connected funnel or checkout
                pages (together, the "Site") are operated by Ieshia Danielle
                ("we," "us," or "our").
              </p>
              <p>
                Your privacy matters to us. This policy explains what
                information we collect when you visit the Site, how we use it,
                who we share it with, and the choices you have. By using the
                Site, you agree to the practices described here.
              </p>
              <p>
                If you have any questions, email us at{" "}
                <a
                  href="mailto:admin@mail.iamieshiadanielle.com"
                  className="text-[#9E1B50] underline"
                >
                  admin@mail.iamieshiadanielle.com
                </a>
                .
              </p>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                1. Information You Give Us
              </h2>
              <p>We collect information you choose to provide when you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Place an order</strong> (workbook, shop products, or
                  the Becoming Intensive): your name, email address, phone
                  number, billing and shipping address, and order details.
                </li>
                <li>
                  <strong>Take the quiz</strong> (Quick Check or Deep Dive):
                  your answers to the assessment questions and your results,
                  along with the name and email you provide to receive them.
                </li>
                <li>
                  <strong>
                    Join our email list or download a free resource
                  </strong>{" "}
                  (such as the Women's Gathering Planning Kit): your name and
                  email address.
                </li>
                <li>
                  <strong>Submit a prayer request:</strong> your name, contact
                  information, and whatever you choose to share in your request.
                </li>
                <li>
                  <strong>Use the Becoming Companion or similar tools:</strong>{" "}
                  any reflections or letters you write and ask us to deliver
                  back to you.
                </li>
                <li>
                  <strong>Submit a speaking or booking inquiry:</strong> your
                  name, contact information, organization and event details, and
                  any materials or video links you include.
                </li>
                <li>
                  <strong>
                    Apply to be a podcast guest or submit your story:
                  </strong>{" "}
                  your name, contact information, your answers to the guest
                  questionnaire, and whatever parts of your story you choose to
                  share. Story submissions are treated with the same care as
                  prayer requests: we never publish your story without your
                  permission, and submissions that are not selected are simply
                  kept private or deleted on request. If you are selected and
                  choose to appear, your recorded appearance is governed by the
                  signed Guest Appearance Agreement.
                </li>
                <li>
                  <strong>Contact us by email, form, or text message:</strong>{" "}
                  your contact information and the contents of your message.
                </li>
              </ul>
              <p>
                You are never required to share more than you're comfortable
                with. Please only include information in prayer requests, quiz
                answers, and messages that you are willing to share with us.
              </p>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                2. Information Collected Automatically
              </h2>
              <p>
                When you visit the Site, we and our service providers
                automatically collect certain information using cookies, pixels,
                and similar technologies, including your IP address, browser and
                device type, the pages you view, the links you click, how you
                arrived at the Site, and general (non-precise) location such as
                city or region.
              </p>
              <p>
                We use the following third-party tools, which may set their own
                cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Meta (Facebook) Pixel</strong> — measures the
                  performance of our ads and may be used to show you relevant
                  ads on Facebook and Instagram.
                </li>
                <li>
                  <strong>Google Analytics and Google Ads</strong> — help us
                  understand how visitors use the Site and measure ad
                  performance.
                </li>
                <li>
                  <strong>Our website and marketing platform</strong> — powers
                  our pages, forms, checkout, email, and text messaging, and
                  tracks form submissions and page visits so we can serve you
                  better.
                </li>
              </ul>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                3. How We Use Your Information
              </h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Process orders, payments, and shipping, and send order
                  confirmations and updates
                </li>
                <li>Deliver your quiz results and recommended next steps</li>
                <li>
                  Send the emails and (with your consent) text messages you've
                  signed up for
                </li>
                <li>
                  Respond to prayer requests, messages, and booking inquiries
                </li>
                <li>
                  Run and improve the Site, our content, and our marketing
                </li>
                <li>Personalize your experience</li>
                <li>
                  Prevent fraud, enforce our Terms, and comply with legal
                  obligations
                </li>
              </ul>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                4. A Note About the Quiz and Prayer Requests
              </h2>
              <p>
                Our quiz is a self-reflection tool, not a medical or
                mental-health assessment. Your answers are used only to generate
                your results and recommend resources that may encourage you.
                Nothing on this Site is a substitute for professional medical,
                mental-health, legal, or financial care.
              </p>
              <p>
                Prayer requests and similar submissions are treated with care
                and confidentiality: they are used only to pray for you, respond
                to you, and follow up with you. We never sell them or use them
                for advertising.
              </p>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                5. Text Messaging (SMS): Opt-In, Message Terms, and Opt-Out
              </h2>
              <p>
                We send text (SMS) messages only to mobile numbers in the United
                States where you have given us your express prior express
                written consent to be contacted. Providing your mobile number
                and checking the consent box, replying with a keyword to a
                campaign short code or long code, or otherwise confirming your
                subscription constitutes your electronic signature and opt-in
                under the Telephone Consumer Protection Act (TCPA) and the
                Carrier Guidelines for A2P 10DLC messaging. Your consent is not
                a condition of any purchase, and you may opt out at any time
                using the methods described below.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.1 How you opt in
              </h3>
              <p>You may opt in to receive SMS messages from us by:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Web form opt-in:</strong> Entering your mobile phone
                  number on our website (for example, on a sign-up form, quiz
                  results page, event registration, or checkout) and checking
                  the consent box that confirms you agree to receive text
                  messages.
                </li>
                <li>
                  <strong>Text-to-join:</strong> Texting a keyword (such as
                  JOIN, START, or a campaign-specific keyword) to the phone
                  number we publish for that campaign. By texting in, you
                  confirm you are the owner or authorized user of the mobile
                  device and consent to receive recurring automated SMS
                  messages.
                </li>
                <li>
                  <strong>Checkout and order opt-in:</strong> Providing your
                  mobile number during a workbook, shop, or Becoming Intensive
                  purchase and selecting the option to receive order and
                  marketing updates by text.
                </li>
                <li>
                  <strong>Verbal or written opt-in:</strong> Providing your
                  mobile number in person at an event, on a prayer request card,
                  or over the phone and confirming you would like to receive
                  texts.
                </li>
              </ul>
              <p>
                When you opt in, you will receive an immediate confirmation
                message confirming your subscription, identifying our brand
                (Ieshia Danielle), and reminding you of the opt-out keyword
                (STOP) and the help keyword (HELP).
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.2 What you are opting in to
              </h3>
              <p>
                By opting in, you consent to receive recurring automated
                marketing and transactional text messages sent using an
                automatic telephone dialing system or prerecorded messages from
                Ieshia Danielle and our service providers. Message types may
                include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Order confirmations, shipping updates, and delivery
                  notifications
                </li>
                <li>
                  Quiz results, resource links, and recommended next steps
                </li>
                <li>
                  Event reminders, workshop announcements, and session links
                </li>
                <li>New podcast episode alerts and content releases</li>
                <li>
                  Promotional offers, product launches, and seasonal messages
                </li>
                <li>Prayer follow-ups and ministry communications</li>
              </ul>
              <p>
                <strong>Message frequency:</strong> Message frequency varies.
                You may receive up to several messages per week. You will not
                receive more than the frequency disclosed at the point of opt-in
                for a given campaign.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.3 Message costs and carrier terms
              </h3>
              <p>
                <strong>Msg &amp; data rates may apply.</strong> Standard
                message and data rates charged by your mobile carrier may apply
                to messages you send to or receive from us. Check your mobile
                plan for details. We are not responsible for any fees your
                carrier may charge you. Messages are delivered via carriers
                participating in the A2P 10DLC ecosystem, and delivery is not
                guaranteed.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.4 How to opt out (STOP)
              </h3>
              <p>
                You can opt out and stop receiving text messages from us at any
                time, free of charge, by any of the following methods:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Reply STOP</strong> to any text message we send you.
                  This immediately cancels your subscription to that campaign.
                </li>
                <li>
                  <strong>Text STOP</strong> to the phone number or short code
                  you used to opt in.
                </li>
                <li>
                  <strong>
                    Reply with STOPALL, END, CANCEL, UNSUBSCRIBE, or QUIT
                  </strong>{" "}
                  — these keywords are also recognized as opt-out commands.
                </li>
                <li>
                  <strong>Email us</strong> at{" "}
                  <a
                    href="mailto:admin@mail.iamieshiadanielle.com"
                    className="text-[#9E1B50] underline"
                  >
                    admin@mail.iamieshiadanielle.com
                  </a>{" "}
                  with your mobile number and the request to be removed.
                </li>
                <li>
                  <strong>Contact us through our website</strong> at
                  iamieshiadanielle.com and request removal of your number.
                </li>
              </ul>
              <p>
                After you opt out, you will receive one final confirmation
                message confirming that you have been unsubscribed and will
                receive no further messages. You will not receive any further
                marketing or automated messages from that campaign unless you
                re-opt in. Transactional messages required to complete an order
                you have already placed (such as a shipping confirmation) may
                still be sent where required by law.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.5 How to get help (HELP)
              </h3>
              <p>You can get help with your subscription at any time by:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Replying HELP</strong> to any message we send, or
                  texting HELP to the number or short code you used to opt in.
                  You will receive a response with our brand name, contact
                  information, a reminder of the opt-out keyword (STOP), and a
                  link to these terms.
                </li>
                <li>
                  <strong>Emailing</strong>{" "}
                  <a
                    href="mailto:admin@mail.iamieshiadanielle.com"
                    className="text-[#9E1B50] underline"
                  >
                    admin@mail.iamieshiadanielle.com
                  </a>
                  .
                </li>
              </ul>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.6 Re-opting in
              </h3>
              <p>
                If you have opted out and later wish to receive messages again,
                you may re-opt in by texting START or the campaign keyword to
                the number or short code, or by completing a new web opt-in form
                and providing your consent again. We will not resume messaging
                you until you have provided fresh express consent.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.7 Number changes, porting, and carrier issues
              </h3>
              <p>
                You are responsible for notifying us if your mobile number
                changes. Text messaging subscriptions are tied to the mobile
                number you provided; if your number is reassigned, ported, or
                transferred, you must re-opt in from the new number and opt out
                from the old number. We are not liable for messages sent to a
                number before we receive notice of a change. If your number is
                recycled and you begin receiving messages you did not request,
                reply STOP to opt out immediately.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.8 Age and eligibility
              </h3>
              <p>
                You must be at least 18 years old, or have the permission of a
                parent or legal guardian, to opt in to SMS messaging. By opting
                in, you represent that you meet this requirement and that you
                are the subscriber or authorized user of the mobile number
                provided.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.9 Privacy and data handling for SMS
              </h3>
              <p>
                Your mobile number and message data are handled in accordance
                with this Privacy Policy and our Terms. We do not sell your
                mobile number. Your number and consent records are shared only
                with our SMS service provider for the purpose of delivering
                messages and maintaining compliance records, and with carriers
                as required by the A2P 10DLC registration process. Consent
                records, including the source, timestamp, and opt-in method, are
                retained to demonstrate compliance with carrier and regulatory
                requirements.
              </p>

              <h3 className="font-bold text-[#380C1E] text-base pt-4">
                5.10 Liability and support
              </h3>
              <p>
                We strive to deliver messages reliably, but we are not liable
                for delayed or undelivered messages caused by carrier filtering,
                network failures, device incompatibility, or other factors
                outside our control. For any SMS-related support, including
                trouble opting in or out, email{" "}
                <a
                  href="mailto:admin@mail.iamieshiadanielle.com"
                  className="text-[#9E1B50] underline"
                >
                  admin@mail.iamieshiadanielle.com
                </a>{" "}
                and we will respond within two business days.
              </p>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                6. Email Marketing
              </h2>
              <p>
                If you join our email list, every marketing email includes an
                unsubscribe link, and you can opt out at any time.
              </p>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                7. How We Share Information
              </h2>
              <p>
                We do not sell your personal information. We share it only with
                service providers who help us run the Site, payment processors,
                and when required by law.
              </p>

              <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
                8. Contact Us
              </h2>
              <p>
                <strong>Ieshia Danielle</strong>
                <br />
                Email:{" "}
                <a
                  href="mailto:admin@mail.iamieshiadanielle.com"
                  className="text-[#9E1B50] underline"
                >
                  admin@mail.iamieshiadanielle.com
                </a>
                <br />
                Website:{" "}
                <a
                  href="https://iamieshiadanielle.com"
                  className="text-[#9E1B50] underline"
                >
                  iamieshiadanielle.com
                </a>
              </p>

              <p className="font-serif italic text-lg text-center text-[#9E1B50] pt-6">
                We're glad you're here. Growth. Healing. Jesus.
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
